import { useMemo } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { Button } from 'antd';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';

import { useGetLMSAccountsDataQuery } from '@/api/endpoints/lmsData';
import { useCreateSchoolMutation } from '@/api/endpoints/schools';
import { DataSection, InputField, SelectField } from '@/shared/components';
import { ROUTES } from '@/shared/consts';
import { useApiRequest } from '@/shared/hooks';
import type { ESchoolType } from '@/shared/types';
import { ESchoolBadgeIcons } from '@/shared/types';

import { IconSelectLabel } from '../_components/IconSelectLabel/IconSelectLabel';
import { CORPORATE_SCHOOL_VALIDATION_SCHEMA } from '../consts';

import styles from './SchoolCreatePage.module.scss';
import type { TCorporateSchoolFormData } from './types';

type TProps = {
  schoolType: ESchoolType;
};

export const CreateCorporateSchoolComponent = ({ schoolType }: TProps) => {
  const navigate = useNavigate();

  const sendApiRequest = useApiRequest();

  const { data: lmsAccountsData = [], isLoading: areLMSAccountsLoading } = useGetLMSAccountsDataQuery();

  const [createSchool, { isLoading: isSchoolCreating }] = useCreateSchoolMutation();

  const methods = useForm<TCorporateSchoolFormData>({
    defaultValues: {
      title: '',
      lmsAccountId: undefined,
      icon: undefined,
    },
    resolver: yupResolver(CORPORATE_SCHOOL_VALIDATION_SCHEMA),
  });

  const { handleSubmit } = methods;

  const lmsAccountsList = useMemo(
    () => lmsAccountsData.map(({ id, name }) => ({ value: id, label: name })),
    [lmsAccountsData],
  );
  const badgeIconsList = useMemo(
    () =>
      Object.values(ESchoolBadgeIcons).map((iconType) => ({
        value: iconType,
        label: <IconSelectLabel iconType={iconType} />,
      })),
    [],
  );

  const goBack = () => {
    navigate(`/${ROUTES.PUBLIC_PAGES}/${ROUTES.SCHOOLS}`);
  };

  const createSchoolHandler = handleSubmit(({ title, icon, lmsAccountId }) => {
    if (!lmsAccountId) {
      return;
    }

    sendApiRequest({
      successMessage: 'Школа успешно создана',
      successCallback: () => void navigate(`/${ROUTES.PUBLIC_PAGES}/${ROUTES.SCHOOLS}`),
      apiRequestCallback: () =>
        createSchool({
          data: {
            name: title,
            lms_account_id: lmsAccountId,
            icon,
            school_type: schoolType,
          },
        }),
    });
  });

  return (
    <div className={styles['content-wrapper']}>
      <FormProvider {...methods}>
        <DataSection
          titleText="Название"
          isTitleRequired
        >
          <InputField
            fieldName="title"
            placeholder="Название"
          />
        </DataSection>
        <DataSection
          titleText="Аккаунт школы в LMS"
          isTitleRequired
        >
          <SelectField
            fieldName="lmsAccountId"
            size="large"
            placeholder="Выбрать аккаунт"
            options={lmsAccountsList}
            showSearch={{
              optionFilterProp: 'label',
            }}
            loading={areLMSAccountsLoading}
            disabled={areLMSAccountsLoading}
          />
        </DataSection>
        <DataSection titleText="Иконка школы">
          <SelectField
            fieldName="icon"
            size="large"
            virtual={false}
            showSearch={false}
            placeholder="Выбрать иконку"
            options={badgeIconsList}
          />
        </DataSection>
        <div className={styles['buttons-wrapper']}>
          <Button
            onClick={goBack}
            type="dashed"
            size="large"
            className={styles.button}
          >
            Назад
          </Button>
          <Button
            onClick={createSchoolHandler}
            type="primary"
            size="large"
            className={styles.button}
            disabled={isSchoolCreating}
          >
            Сохранить
          </Button>
        </div>
      </FormProvider>
    </div>
  );
};
